import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { FormlySelectModule } from '@ngx-formly/core/select';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
    standalone: true,
    selector: 'formly-separate-select',
    imports: [CommonModule, ReactiveFormsModule, FormlyModule, FormlySelectModule, MatSelectModule, MatFormFieldModule],
    template: `
    <label class="field-label-outside">
      {{ to.label }} <span *ngIf="to.required" class="required">*</span>
    </label>

    <mat-form-field appearance="outline" class="field-select-wrapper" floatLabel="never">
      <mat-select
        [placeholder]="to.label"
        [formControl]="formControl"
        [formlyAttributes]="field"
        [compareWith]="to.compareWith || defaultCompare"
        [multiple]="to.multiple || false"
        [required]="to.required || null"
        [attr.aria-describedby]="to.description ? ('hint_' + field.id) : null"
        [ngClass]="{ invalid: showError }"
      >
        <mat-option *ngIf="!to.multiple" [value]="undefined" disabled>
          Select {{ to.label }}
        </mat-option>

        <mat-option *ngFor="let opt of (to.options | formlySelectOptions: field | async)" [value]="opt.value">
          {{ opt.label }}
        </mat-option>
      </mat-select>

      <mat-hint *ngIf="to.description" [id]="'hint_' + field.id">
        {{ to.description }}
      </mat-hint>

      <mat-error *ngIf="showError && !isEmpty()">
        <formly-validation-message [field]="field"></formly-validation-message>
      </mat-error>
    </mat-form-field>
  `,
    styles: [`
    .field-label {
      display: block;
      margin-bottom: 6px;
      font-weight: 600;
      font-size: 14px;
    }
    .required {
      color: #dc2626;
      margin-left: 2px;
    }
    .field-select {
      width: 100%;
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      background: white;
      outline: none;
      transition: border-color 120ms ease-in-out, box-shadow 120ms ease-in-out;
      font-size: 14px;
      font-family: inherit;
    }
    .field-select:focus {
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
    }
    .field-select.invalid {
      border-color: #dc2626;
    }
    .field-error {
      margin-top: 4px;
      color: #dc2626;
      font-size: 12px;
    }
    .field-hint {
      margin-top: 4px;
      color: #6b7280;
      font-size: 12px;
    }
    .field-select-wrapper {
      width: 100%;
      font-size: 14px;
      font-family: inherit;
      height: 40px;
      margin-top: 5px;
      margin-bottom: 12px;
    }

    .field-select-wrapper .mat-select-trigger {
      min-height: 40px;
      height: 40px;
      padding: 0 8px;
      display: flex;
      align-items: center;
      font-size: 13px; 
    }
    .field-select-wrapper.mat-form-field-invalid .mat-form-field-outline {
      color: #dc2626;
      border-color: #dc2626;
    }
    .field-label-outside {
      display: block;
      font-weight: 600;
      font-size: 14px;
      margin-top: 12px;
    }

    .field-select-wrapper.mat-form-field {
      width: 100%;
    }

    .mat-select-placeholder {
      color: #999;
    }

    .mat-form-field-appearance-outline .mat-form-field-outline {
      border-color: #ccc;
    }

    .mat-form-field-appearance-outline.mat-form-field-invalid .mat-form-field-outline {
      border-color: #dc2626;
    }

    .field-select-wrapper.mat-form-field {
      height: 40px;
    }

    .field-select-wrapper .mat-form-field-flex {
      height: 40px;
      min-height: 40px;
      display: flex;
      align-items: center;
    }

   
  `]
})
export class SeparateSelectType extends FieldType {
    defaultCompare = (o1: any, o2: any): boolean => {
        if (o1 === o2) return true;
        if (!o1 || !o2) return false;

        const k1 = typeof o1 === 'object' ? (o1.key ?? o1.value ?? o1.id ?? o1) : o1;
        const k2 = typeof o2 === 'object' ? (o2.key ?? o2.value ?? o2.id ?? o2) : o2;
        try { return JSON.stringify(k1) === JSON.stringify(k2); } catch { return k1 === k2; }
    };

    isEmpty(): boolean {
        const v = this.formControl?.value;
        if (v == null) return true;
        if (Array.isArray(v)) return v.length === 0;
        if (typeof v === 'string') return v.trim() === '';
        return false;
    }
}